import { Flex, Grid, Group, Tooltip } from '@mantine/core';
import type { AuditItemOut } from '@repo/api-codegen';
import { INCIDENT_ICON_MAPPING } from '@repo/common/constants';
import { Icon, Text } from '@repo/foundations';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { ResourceLink } from '../../../components/Link';
import { formatShortTimeDifference } from '../../../utils/time';
import { useStyles } from './IncidentAuditItem.styles';

export interface IncidentAuditItemProps {
	auditItem: AuditItemOut;
	showLine?: boolean;
}

function IncidentAuditItem({
	auditItem,
	showLine = false,
}: IncidentAuditItemProps) {
	const { classes } = useStyles();

	const formattedTime = `${formatShortTimeDifference(auditItem.created_at)}`;

	let icon: ReactNode = <Icon name="alertCircleFilled" />;
	let text: ReactNode = <Text size="sm" />;

	const actorId = auditItem.actor?.id;
	const actorType = auditItem.actor_type;
	const currId = auditItem.extra?.current_value;
	const prevId = auditItem.extra?.previous_value;

	switch (auditItem.event_type) {
		case 'incident.new':
			icon = (
				<Icon name="alertTriangleFilled" fillColor="icon/warning/default" />
			);
			text = <Text size="sm">Incident started</Text>;
			break;
		case 'incident.resolved':
			icon = (
				<Icon
					size="md"
					name={INCIDENT_ICON_MAPPING['resolved'].iconName}
					fillColor={INCIDENT_ICON_MAPPING['resolved'].iconFillColor}
					color={INCIDENT_ICON_MAPPING['resolved'].iconColor}
				/>
			);
			if (actorType === 'system') {
				text = <Text size="sm">Incident was automatically resolved</Text>;
			} else if (actorId) {
				text = (
					<Text size="sm">
						Incident resolved by <ResourceLink href={`/user/${actorId}`} />
					</Text>
				);
			} else {
				text = <Text size="sm">Incident was resolved</Text>;
			}
			break;
		case 'incident.normal':
			icon = <Icon name="circleCheck" color="icon/secondary/default" />;
			if (actorId) {
				text = (
					<Text size="sm">
						Incident marked normal by <ResourceLink href={`/user/${actorId}`} />
					</Text>
				);
			} else {
				text = <Text size="sm">Incident was marked as normal</Text>;
			}
			break;
		case 'incident.acknowledged':
			icon = (
				<Icon
					size="md"
					name={INCIDENT_ICON_MAPPING['acknowledged'].iconName}
					fillColor={INCIDENT_ICON_MAPPING['acknowledged'].iconFillColor}
					color={INCIDENT_ICON_MAPPING['acknowledged'].iconColor}
				/>
			);
			text = (
				<Text size="sm">
					Incident acknowledged by <ResourceLink href={`/user/${actorId}`} />
				</Text>
			);
			break;
		case 'incident.expired':
			icon = (
				<Icon
					size="md"
					name={INCIDENT_ICON_MAPPING['expired'].iconName}
					fillColor={INCIDENT_ICON_MAPPING['expired'].iconFillColor}
					color={INCIDENT_ICON_MAPPING['expired'].iconColor}
				/>
			);
			text = <Text size="sm">Incident expired</Text>;
			break;
		case 'incident.assigned_owner':
			icon = <Icon name="userPlus" />;

			if (currId === actorId) {
				text = (
					<Text size="sm">
						<ResourceLink href={`/user/${actorId}`} /> self-assigned as{' '}
						<b>owner</b>
					</Text>
				);
			} else if (prevId && currId) {
				text = (
					<Text size="sm">
						<ResourceLink href={`/user/${actorId}`} /> re-assigned{' '}
						<ResourceLink href={`/user/${currId}`} /> as <b>owner</b>
					</Text>
				);
			} else if (!prevId && currId) {
				text = (
					<Text size="sm">
						<ResourceLink href={`/user/${actorId}`} /> assigned{' '}
						<ResourceLink href={`/user/${currId}`} /> as <b>owner</b>
					</Text>
				);
			} else if (prevId && !currId) {
				text = (
					<Text size="sm">
						<ResourceLink href={`/user/${actorId}`} /> removed the <b>owner</b>
					</Text>
				);
			}
			break;
		default:
			return null;
	}

	return (
		<Grid pt={0}>
			<Grid.Col span={2} className={classes.icon}>
				<Flex direction="column" align="center" gap="3xs">
					{icon}
					{showLine && <div className={classes.line} />}
				</Flex>
			</Grid.Col>
			<Grid.Col span="auto" className={classes.content}>
				<Group spacing="xs" noWrap>
					{text}
					<Tooltip label={dayjs(auditItem.created_at).format('LLL')}>
						<Text size="xs" color="text/secondary/default" truncate>
							{formattedTime}
						</Text>
					</Tooltip>
				</Group>
			</Grid.Col>
		</Grid>
	);
}

export default IncidentAuditItem;
